import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { localStorage } from 'dibs-browser-storage';
import { Carousel } from 'dibs-carousel';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { Button } from 'dibs-elements/exports/Button';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalBody } from 'dibs-elements/exports/ModalBody';

import { PDP_TRADE_DESIGN_MANAGER_ONBOARDING_VIEW_COUNT_STORAGE_KEY } from '../../global/tradeOnboarding/constants';

import styles from './PdpRespTradeDesignManagerOnboardingModal.scss';

type designManagerOnboardingModalStepsType = {|
    image: string,
    heading: React.Element,
    text: React.Element,
|};

type ComponentProps = {|
    onPrimaryCtaClick: () => {},
|};

const designManagerOnboardingModalSteps: Array<designManagerOnboardingModalStepsType> = [
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/trade/design-manager/design-manager-step-1.jpg',
        heading: (
            <FormattedMessage
                id="abf.PdpRespTradeDesignManagerOnboardingModal.designManagerStep1Heading"
                defaultMessage="Quickly Add Items To Design Manager"
            />
        ),
        text: (
            <FormattedMessage
                id="abf.PdpRespTradeDesignManagerOnboardingModal.designManagerStep1Text"
                defaultMessage="Your 1stdibs items will be available in your Design Manager account"
            />
        ),
    },
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/trade/design-manager/design-manager-step-2.jpg',
        heading: (
            <FormattedMessage
                id="abf.PdpRespTradeDesignManagerOnboardingModal.designManagerStep2Heading"
                defaultMessage="Review Your 1stdibs Item"
            />
        ),
        text: (
            <FormattedMessage
                id="abf.PdpRespTradeDesignManagerOnboardingModal.designManagerStep2Text"
                defaultMessage="Look out for the Design Manager button on each item page on 1stdibs"
            />
        ),
    },
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/trade/design-manager/design-manager-step-3.jpg',
        heading: (
            <FormattedMessage
                id="abf.PdpRespTradeDesignManagerOnboardingModal.designManagerStep3Heading"
                defaultMessage="See It In Your Account"
            />
        ),
        text: (
            <FormattedMessage
                id="abf.PdpRespTradeDesignManagerOnboardingModal.designManagerStep3Text"
                defaultMessage="Select 1stdibs as your vendor and your Design Manager project"
            />
        ),
    },
];

const MAX_STEPS = designManagerOnboardingModalSteps.length;

export const PdpRespTradeDesignManagerOnboardingModal = (props: ComponentProps) => {
    const [isOpen, setIsOpen] = useState(true);
    const [currentStepIndex, setStepIndex] = useState(0);
    const [arrowClick, setArrowClick] = useState(false);

    const { image, heading, text } = designManagerOnboardingModalSteps[currentStepIndex];

    /* This modal belongs to the onboarding flow and should not be displayed more than once
    PDP_TRADE_DESIGN_MANAGER_ONBOARDING_VIEW_COUNT_STORAGE_KEY does not have to update at every render */
    useEffect(() => {
        const viewCount =
            localStorage.getItem(PDP_TRADE_DESIGN_MANAGER_ONBOARDING_VIEW_COUNT_STORAGE_KEY) || 0;
        localStorage.setItem(
            PDP_TRADE_DESIGN_MANAGER_ONBOARDING_VIEW_COUNT_STORAGE_KEY,
            viewCount + 1
        );
    }, []);

    return (
        <ModalContainer
            closeOnOutsideClick={false}
            isOpen={isOpen}
            modalPosition="center"
            onClose={() => setIsOpen(false)}
            preventBackScroll
        >
            <ModalCloseButton onClick={() => setIsOpen(false)} />
            <div className={styles.modalTitle}>
                <BarHeader
                    title={
                        <FormattedMessage
                            defaultMessage="1stdibs and Design Manager"
                            id="abf.PdpRespTradeDesignManagerOnboardingModal.title"
                        />
                    }
                />
            </div>
            <ModalBody className={styles.modalBody}>
                <div className={styles.modalTitle}>
                    <FormattedMessage
                        id="abf.PdpRespTradeDesignManagerOnboardingModal.bodyHeader"
                        defaultMessage="We're excited to share the news with you! 1stidbs and Design Manager have entered into an agreement to merge."
                    />
                    <div>
                        <a
                            href="https://www.1stdibs.com/info/design-manager/"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-tn="design-manager-announcement-link"
                        >
                            <FormattedMessage
                                id="abf.PdpRespTradeDesignManagerOnboardingModal.announcementLink"
                                defaultMessage="Learn more about this announcement."
                            />
                        </a>
                    </div>
                </div>
                <div className={styles.carouselBody}>
                    <div className={styles.heading}>{heading}</div>
                    <div className={styles.text}>{text}</div>
                    <Carousel
                        itemKey={image}
                        dataTn="pdp-trade-design-manager-onboarding-carousel"
                        totalItems={MAX_STEPS}
                        dotsPerPage={MAX_STEPS}
                        onIndexChange={({ index }) => setStepIndex(index)}
                        renderItem={() => {
                            return <img className={styles.imgContainer} src={image} />;
                        }}
                        showDots
                    />
                </div>
                <Button
                    onClick={() => {
                        setIsOpen(false);
                        props.onPrimaryCtaClick();
                    }}
                    dataTn="design-manager-onboarding-modal-show-me-how-cta"
                    fullWidth
                >
                    <FormattedMessage
                        id="abf.PdpRespTradeDesignManagerOnboardingModal.showMeHowCta"
                        defaultMessage="Show Me How"
                    />
                </Button>
            </ModalBody>
        </ModalContainer>
    );
};
